import React, { useState, useEffect, useRef, useCallback, useMemo, memo, Suspense, lazy } from 'react';
import { Link, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import Logo from './Logo';
import { safeEncodeURIComponent } from '../utils/urlEncoding';
import './NavBar.css';
import './NavBar-favorites.css';
// Import the countries data
import { countries } from '../utils/countries';

// Dynamically import GoogleOAuthProvider
const GoogleOAuthProvider = lazy(() => 
  import('@react-oauth/google').then(module => ({ default: module.GoogleOAuthProvider }))
);

// Dynamically import the new GoogleSignInButton component
const GoogleSignInButton = lazy(() => import('./GoogleSignInButton'));

const isReactSnap = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap'; // Check for ReactSnap

// Memoize the NavLink item to prevent re-renders when parent state changes
const MemoizedNavItem = memo(({ to, onClick, children, selectedCountry }) => (
  <li>
    <NavLink
      to={to}
      className={({ isActive }) => (isActive ? 'active' : '')}
      onClick={onClick}
    >
      {children}
    </NavLink>
  </li>
));

// Memoize search form component
const SearchForm = memo(({ searchQuery, setSearchQuery, handleSearchSubmit }) => (
  <form className="search-form" onSubmit={handleSearchSubmit}>
    <input
      type="text"
      placeholder="Search products..."
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
    />
    <button type="submit" aria-label="Search">
      <svg className="search-icon" viewBox="0 0 24 24">
        <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
      </svg>
    </button>
  </form>
));

function NavBar({ selectedCountry }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, setUser } = useUser();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [imageLoadError, setImageLoadError] = useState(false);
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const navMenuRef = useRef(null);
  const userDropdownRef = useRef(null);
  const hamburgerRef = useRef(null);
  const mobileBtnRef = useRef(null);
  
  // Get the country flag
  const countryData = useMemo(() => 
    countries.find(c => c.code === selectedCountry?.toLowerCase()),
    [selectedCountry]
  );
  
  const flagUrl = countryData ? countryData.flag : null;
  
  // Memoize derived values
  const isSearchPage = useMemo(() => 
    location.pathname.includes('/search'),
    [location.pathname]
  );

  // Determine if the current page is the Home page
  const isHomePage = useMemo(() =>
    location.pathname.endsWith('/home') || location.pathname === '/', // Adjust as per your routing for home
    [location.pathname]
  );

  // Reset search query when we navigate to a new page or when the search params change
  useEffect(() => {
    setSearchQuery('');
  }, [location.pathname]);

  // Update search query if we're on search page
  useEffect(() => {
    if (isSearchPage) {
      const q = searchParams.get('q');
      if (q) {
        setSearchQuery(q);
      }
    }
  }, [isSearchPage, searchParams]);

  // Convert toggle functions to useCallback to prevent recreation on renders
  const toggleMobileMenu = useCallback((e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    setMobileMenuOpen(prevState => {
      const newState = !prevState;
      document.body.style.overflow = newState ? 'hidden' : 'auto';
      return newState;
    });
  }, []);

  const closeMenu = useCallback((e) => {
    if (e) {
      e.stopPropagation();
    }
    setMobileMenuOpen(false);
    document.body.style.overflow = 'auto';
  }, []);

  // Handle clicks outside navbar
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        (hamburgerRef.current && hamburgerRef.current.contains(event.target)) ||
        (mobileBtnRef.current && mobileBtnRef.current.contains(event.target))
      ) {
        return;
      }
      
      if (navMenuRef.current && !navMenuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
        document.body.style.overflow = 'auto';
      }
      
      if (userDropdownRef.current && 
          !userDropdownRef.current.contains(event.target) &&
          !event.target.closest('.user-menu-button')) {
        setShowUserDropdown(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Memoized handlers
  const handleSearchSubmit = useCallback((e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      const trimmedQuery = searchQuery.trim();
      
      // Track search in Google Analytics
      if (window.gtag) {
        window.gtag('event', 'search', {
          event_category: 'Search',
          event_label: `Search Term: ${trimmedQuery}`,
          search_term: trimmedQuery,
          search_source: 'navbar',
          country_code: selectedCountry
        });
      }
      
      // Save navigation state
      sessionStorage.setItem('fromSearch', 'true');
      sessionStorage.setItem('productReferrer', window.location.pathname + window.location.search);
      
      // Use window.location.href instead of navigate for a full page refresh
      window.location.href = `/country/${selectedCountry}/search?q=${safeEncodeURIComponent(trimmedQuery)}`;
      
      // The following lines will only execute if the page doesn't immediately refresh
      setSearchQuery('');
      setMobileMenuOpen(false);
    }
  }, [searchQuery, selectedCountry]);

  const logout = useCallback(() => {
    setUser(null);
    setShowUserDropdown(false);
  }, [setUser]);

  const toggleUserDropdown = useCallback(() => {
    setShowUserDropdown(prev => !prev);
  }, []);

  // Get user initials - memoized to prevent recalculation
  const userInitials = useMemo(() => {
    if (!user || !user.name) return '';
    const names = user.name.split(' ');
    if (names.length === 1) return names[0].charAt(0);
    return `${names[0].charAt(0)}${names[names.length - 1].charAt(0)}`;
  }, [user]);

  // Memoize nav items array
  const navItems = useMemo(() => {
    return ['home', 'products', 'about', 'blog', 'contact'].map((page) => {
      const handleNavClick = (e) => {
        e.stopPropagation();
        closeMenu();
      };
      
      const capitalized = page.charAt(0).toUpperCase() + page.slice(1);
      
      return (
        <MemoizedNavItem 
          key={page} 
          to={`/country/${selectedCountry}/${page}`} 
          onClick={handleNavClick}
          selectedCountry={selectedCountry}
        >
          {page === 'home' ? 'HOME' : capitalized.toUpperCase()}
        </MemoizedNavItem>
      );
    });
  }, [selectedCountry, closeMenu]);
  
  // Right Section Content
  const rightSectionContent = (
    <>
      {/* Conditionally render search bar based on whether it's the Home page */}
      {!isHomePage && !isSearchPage && (
        <div className="desktop-search-bar-wrapper">
          <SearchForm 
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            handleSearchSubmit={handleSearchSubmit}
          />
        </div>
      )}
      
      {/* Country Flag - Added before user profile/Google sign-in */}
      {flagUrl && (
        <div className="navbar-flag-container">
          <img
            src={flagUrl}
            alt={`${selectedCountry.toUpperCase()} flag`}
            className="navbar-flag"
          />
        </div>
      )}

      {user ? (
        <div className="user-menu-container">
          <button className="user-menu-button" onClick={toggleUserDropdown} aria-label="User menu">
            {user.picture && !imageLoadError ? (
              <img 
                src={user.picture} 
                alt={user.name} 
                className="user-avatar" 
                onError={() => setImageLoadError(true)}
              />
            ) : (
              <div className="user-initials">{userInitials}</div>
            )}
            <span className="dropdown-arrow desktop-only">▼</span>
          </button>
          
          {showUserDropdown && (
            <div className="user-dropdown" ref={userDropdownRef}>
              <div className="dropdown-user-name">{user.name}</div>
              <Link 
                to={`/country/${selectedCountry}/favorites`} 
                className="dropdown-menu-item"
                onClick={() => setShowUserDropdown(false)}
              >
                <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" style={{marginRight: '8px'}}>
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
                Favorites
              </Link>
              <button className="logout-button" onClick={logout}>Log out</button>
            </div>
          )}
        </div>
      ) : !isReactSnap ? ( // Conditionally render GoogleSignInButton
        // Use the lazy-loaded GoogleSignInButton component
        // This replaces the previous direct button and ensures sign-in logic is encapsulated
        <Suspense fallback={<div>Loading Sign In...</div>}>
          <GoogleSignInButton setUser={setUser} />
        </Suspense>
      ) : (
        <div>{/* Placeholder for GSI during prerender */}</div>
      )}
    </>
  );
  
  if (!selectedCountry) return null;

  if (isReactSnap) { // If react-snap, render a simplified version without GoogleOAuthProvider
    return (
      <>
        {/* Special mobile hamburger button - always visible on mobile */}
        <button 
          ref={mobileBtnRef}
          className={`hamburger-menu-mobile ${mobileMenuOpen ? 'menu-open' : ''}`}
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
        >
          ☰
        </button>

        <header className="navbar">
          <div className="navbar-container">
            {/* Logo */}
            <div className="logo-wrapper">
              <Link 
                to={`/country/${selectedCountry}/home`} 
                onClick={closeMenu}
              >
                <Logo selectedCountry={selectedCountry} />
              </Link>
            </div>

            {/* Desktop Navigation Links */}
            <nav className="desktop-nav">
              <ul>
                {navItems}
              </ul>
            </nav>

            {/* Regular Hamburger Menu Button - for tablet/desktop */}
            <div className="hamburger-wrapper">
              <button 
                ref={hamburgerRef}
                className="hamburger-button"
                onClick={toggleMobileMenu}
                aria-expanded={mobileMenuOpen}
                aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              >
                <span className="hamburger-icon">☰</span>
              </button>
            </div>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
              <div 
                className={`menu-overlay ${mobileMenuOpen ? 'active' : ''}`} 
                onClick={toggleMobileMenu}
              ></div>
            )}

            {/* Mobile Menu */}
            <nav 
              ref={navMenuRef}
              className={`nav-menu ${mobileMenuOpen ? 'menu-open' : ''}`}
            >
              <div className="mobile-nav-header">
                <Link 
                  to={`/country/${selectedCountry}/home`} 
                  onClick={closeMenu}
                >
                  <Logo selectedCountry={selectedCountry} />
                </Link>
                <button 
                  className="close-menu-button"
                  onClick={toggleMobileMenu}
                  aria-label="Close menu"
                >
                  ✕
                </button>
              </div>

              {/* Mobile Search - Always show in hamburger menu for both mobile and tablet */}
              <div className="mobile-search-form-container">
                <SearchForm 
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  handleSearchSubmit={handleSearchSubmit}
                />
              </div>

              <ul>
                {navItems}
                {/* User-specific mobile items can be conditionally rendered here if needed,
                    but GSI dependent parts are already excluded by the main isReactSnap check */}
              </ul>
            </nav>

            {/* Search Bar and User Profile - Right Side */}
            <div className="right-section">
              {rightSectionContent} {/* This will render the version without GSI button */}
            </div>
          </div>
        </header>
      </>
    );
  }

  // Original return for non-react-snap environments
  return (
    // Wrap the entire NavBar content (or at least the part that needs Google OAuth)
    // with GoogleOAuthProvider and its Suspense boundary.
    <Suspense fallback={<div>Loading Authentication...</div>}>
      <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
        <>
          {/* Special mobile hamburger button - always visible on mobile */}
          <button 
            ref={mobileBtnRef}
            className={`hamburger-menu-mobile ${mobileMenuOpen ? 'menu-open' : ''}`}
            onClick={toggleMobileMenu}
            aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          >
            ☰
          </button>        <header className="navbar">
          <div className="navbar-container">
            {/* Logo */}
            <div className="logo-wrapper">
              <Link 
                to={`/country/${selectedCountry}/home`} 
                onClick={closeMenu}
              >
                <Logo selectedCountry={selectedCountry} />
              </Link>
            </div>

              {/* Desktop Navigation Links */}
              <nav className="desktop-nav">
                <ul>
                  {navItems}
                </ul>
              </nav>

              {/* Regular Hamburger Menu Button - for tablet/desktop */}
              <div className="hamburger-wrapper">
                <button 
                  ref={hamburgerRef}
                  className="hamburger-button"
                  onClick={toggleMobileMenu}
                  aria-expanded={mobileMenuOpen}
                  aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
                >
                  <span className="hamburger-icon">☰</span>
                </button>
              </div>

              {/* Mobile Menu Overlay */}
              {mobileMenuOpen && (
                <div 
                  className={`menu-overlay ${mobileMenuOpen ? 'active' : ''}`} 
                  onClick={toggleMobileMenu}
                ></div>
              )}

              {/* Mobile Menu */}
              <nav 
                ref={navMenuRef}
                className={`nav-menu ${mobileMenuOpen ? 'menu-open' : ''}`}
              >
                <div className="mobile-nav-header">
                  <Link 
                    to={`/country/${selectedCountry}/home`} 
                    onClick={closeMenu}
                  >
                    <Logo selectedCountry={selectedCountry} />
                  </Link>
                  <button 
                    className="close-menu-button"
                    onClick={toggleMobileMenu}
                    aria-label="Close menu"
                  >
                    ✕
                  </button>
                </div>

                {/* Mobile Search - Always show in hamburger menu for both mobile and tablet */}
                <div className="mobile-search-form-container">
                  <SearchForm 
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                    handleSearchSubmit={handleSearchSubmit}
                  />
                </div>

                <ul>
                  {navItems}
                  {user && (
                    <li className="mobile-only-menu-item">
                      <NavLink
                        to={`/country/${selectedCountry}/favorites`}
                        className={({ isActive }) => (isActive ? 'active' : '')}
                        onClick={closeMenu}
                      >
                        <span className="nav-icon">
                          <svg viewBox="0 0 24 24" width="18" height="18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                          </svg>
                        </span>
                        FAVORITES
                      </NavLink>
                    </li>
                  )}
                </ul>
              </nav>

              {/* Search Bar and User Profile - Right Side */}
              <div className="right-section">
                {rightSectionContent}
              </div>
            </div>
          </header>
        </>
      </GoogleOAuthProvider>
    </Suspense>
  );
}

export default memo(NavBar);
